import React, { useContext, useEffect, useState } from "react";
import Styles from "../styles/Reports.module.css";
import { useNavigate } from "react-router-dom";
import { postsAPI } from "../services/api";
import { UserContext } from "./context/user/UserContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faTrashAlt, 
  faTimes, 
  faCheck, 
  faArrowLeft, 
  faExclamationTriangle,
  faUserShield 
} from "@fortawesome/free-solid-svg-icons";
import EmptyState from "./EmptyState";
import Loader from "./Loader";
import Toast from "./Toast";

const ReportedUsers = () => {
  const { userData } = useContext(UserContext);
  const token = userData?.token;
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [toast, setToast] = useState(null);
  
  const getposts = async () => {
    setLoading(true);
    try {
      // Use Supabase API to get reported posts
      const data = await postsAPI.getReportedPosts();
      setPosts(data || []);
    } catch (error) {
      console.error("Error in getting reported posts:", error);
      setToast({ message: `Failed to load reported posts: ${error.message}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    getposts();
  }, []);
  
  const handleDelete = async (postId) => {
    if (deleteConfirmId === postId) {
      setLoading(true);
      setToast({ message: 'Deleting post...', type: 'info' });
      
      try {
        // Use Supabase API to delete post
        await postsAPI.deletePost(token, postId);
        
        setToast({ message: 'Post deleted successfully! ✅', type: 'success' });
        getposts();
        setDeleteConfirmId(null);
      } catch (error) {
        console.error("Error in deleting post:", error);
        setToast({ message: `Failed to delete post: ${error.message}`, type: 'error' });
      } finally {
        setLoading(false);
      }
    } else {
      setDeleteConfirmId(postId);
    }
  };
  
  const cancelDelete = () => {
    setDeleteConfirmId(null);
  };
  
  // Format ID for better readability
  const formatId = (id) => {
    if (!id) return "";
    return `${id.substring(0, 8)}...${id.substring(id.length - 4)}`;
  };

  return (
    <div className={Styles.container}>
      <div className={Styles.heading}>
        <h2>Reported Posts</h2>
        {!loading && (
          <span className={Styles.reportsCount}>
            <FontAwesomeIcon icon={faExclamationTriangle} /> {posts?.length || 0} reported posts
          </span>
        )}
      </div>
      
      <button
        className={Styles.back_button}
        onClick={() => navigate("/admin")}
        aria-label="Return to dashboard"
      >
        <FontAwesomeIcon icon={faArrowLeft} /> Back to Dashboard
      </button>
      
      <div className={Styles.posts_list}>
        {loading ? (
          <div className={Styles.loadingContainer}>
            <Loader />
            <p>Loading reported posts...</p>
          </div>
        ) : posts && posts.length > 0 ? (
          <table aria-label="Reported posts" role="grid">
            <caption className={Styles.srOnly}>List of reported posts</caption>
            <thead>
              <tr>
                <th scope="col" className={Styles.indexColumn}>#</th>
                <th scope="col" className={Styles.idColumn}>Post ID</th>
                <th scope="col" className={Styles.reasonsColumn}>Reasons</th>
                <th scope="col" className={Styles.userIdColumn}>User ID</th>
                <th scope="col" className={Styles.actionColumn}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((post, index) => (
                <tr key={post.id}>
                  <td className={Styles.indexColumn}>{index + 1}</td>
                  <td className={Styles.idColumn} title={post.id}>{formatId(post.id)}</td>
                  <td className={Styles.reasonsColumn}>
                    {post.report_reasons && post.report_reasons.map((reason, idx) => (
                      <span key={idx} className={Styles.reasonTag}>
                        {reason}
                      </span>
                    ))}
                  </td>
                  <td className={Styles.userIdColumn} title={post.posted_by}>
                    {formatId(post.posted_by)}
                    {post.profile?.username && (
                      <div className={Styles.username}>@{post.profile.username}</div>
                    )}
                  </td>
                  <td className={Styles.actionColumn}>
                    {deleteConfirmId === post.id ? (
                      <div className={Styles.confirmButtons}>
                        <button
                          className={`${Styles.confirm_button} ${Styles.confirm_yes}`}
                          onClick={() => handleDelete(post.id)}
                          title="Confirm delete"
                        >
                          <FontAwesomeIcon icon={faCheck} /> Yes
                        </button>
                        <button
                          className={`${Styles.confirm_button} ${Styles.confirm_no}`}
                          onClick={cancelDelete}
                          title="Cancel delete"
                        >
                          <FontAwesomeIcon icon={faTimes} /> No
                        </button>
                      </div>
                    ) : (
                      <button
                        className={Styles.delete_button}
                        onClick={() => handleDelete(post.id)}
                        title="Delete post"
                      >
                        <FontAwesomeIcon icon={faTrashAlt} /> Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState
            icon={faUserShield}
            message="No reported posts found. Everything looks good!"
          />
        )}
      </div>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};

export default ReportedUsers;
